import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  Typography,
} from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import React, { useCallback, useMemo, useState, useEffect } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";

import { uploadBanner } from "../auth/service";
import { useRef } from "react";
import { toast } from "react-toastify";
import CircularProgress from "@mui/material/CircularProgress";

const BannerAccordion = ({ title, data, setData, pageName }) => {
  const [isUploadingImg, setIsUploadingImg] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  // const [error, setError] = useState("");
  const [errors, setErrors] = useState({
    webImages: "",
    mobileImages: "",
    webVideos: "",
    mobileVideos: "",
  });

  const [editIndex, setEditIndex] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [onConfirm, setOnConfirm] = useState(null);

  const [uploadingVideo, setUploadingVideo] = useState({
    webVideos: false,
    mobileVideos: false,
  });

  const webImageRef = useRef(null);
  const mobileImageRef = useRef(null);
  const webVideoRef = useRef(null);
  const mobileVideoRef = useRef(null);
  const editorRef = useRef(null);
  const isEditorInitialized = useRef(false);
  const isEditorReady = useRef(false);

  const isImageFile = (file) => file.type.startsWith("image/");
  const isVideoFile = (file) => file.type.startsWith("video/");

  const hasSyncedFromApi = useRef(false);

  useEffect(() => {
    if (!editorRef.current) return;

    // ✅ Only sync when content comes from API (details page)
    if (!hasSyncedFromApi.current && data?.seoContent) {
      editorRef.current.setContents(data.seoContent);
      editorRef.current.core.focusEdge();
      hasSyncedFromApi.current = true;
    }
  }, [data?.seoContent]);

  useEffect(() => {
    if (pageName && !data.pageName) {
      setData((prev) => ({
        ...prev,
        pageName,
      }));
    }
  }, [pageName]);

  const handleImageUpload = (key) => async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // ❌ Validate before upload
    const invalidFiles = files.filter((file) => !isImageFile(file));

    if (invalidFiles.length) {
      setErrors((prev) => ({
        ...prev,
        [key]: "Invalid image format. Only JPG, PNG, WEBP allowed",
      }));
      e.target.value = "";
      return;
    }

    setErrors((prev) => ({ ...prev, [key]: "" }));
    try {
      setIsUploadingImg(true);
      setErrors("");

      const uploaded = [];

      for (const file of files) {
        const uploadData = new FormData();
        uploadData.append("banner_or_video", file);
        uploadData.append("type", "image");
        uploadData.append("page_name", data.pageName || "Home");

        const res = await uploadBanner(uploadData);

        if (res?.error) {
          toast.error(res.message || "Image upload failed");
          continue;
        }

        if (res?.data?.url) uploaded.push(res.data.url);
      }

      if (uploaded.length) {
        setData((prev) => ({
          ...prev,
          [key]: [...(prev[key] || []), ...uploaded],
        }));
      }
    } catch {
      toast.error("Image upload failed");
    } finally {
      setIsUploadingImg(false);
      e.target.value = "";
    }
  };

  const handleVideoUpload = (key) => async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // ❌ Validate before upload
    const invalidFiles = files.filter((file) => !isVideoFile(file));

    if (invalidFiles.length) {
      setErrors((prev) => ({
        ...prev,
        [key]: "Invalid video format. Only MP4, WEBM, MOV allowed",
      }));
      e.target.value = "";
      return;
    }

    setErrors((prev) => ({ ...prev, [key]: "" }));

    try {
      setUploadingVideo((prev) => ({ ...prev, [key]: true }));
      setErrors("");

      const uploaded = [];

      for (const file of files) {
        const uploadData = new FormData();
        uploadData.append("banner_or_video", file);
        uploadData.append("type", "video");
        uploadData.append("page_name", data.pageName || "Home");

        const res = await uploadBanner(uploadData);

        if (res?.error) {
          toast.error(res.message || "Video upload failed");
          continue;
        }

        if (res?.data?.url) uploaded.push(res.data.url);
      }

      if (uploaded.length) {
        setData((prev) => ({
          ...prev,
          [key]: [...(prev[key] || []), ...uploaded],
        }));
      }
    } catch {
      toast.error("Video upload failed");
    } finally {
      setUploadingVideo((prev) => ({ ...prev, [key]: false }));
      e.target.value = "";
    }
  };

  const handleDeleteImage = () => {
    setData((prev) => ({
      ...prev,
      imageSrc: "",
    }));
  };

  const handleDeleteVideo = () => {
    setData((prev) => ({
      ...prev,
      videoSrc: "",
    }));
  };

  /* ---------------- SunEditor Options ---------------- */
  const editorOptions = useMemo(
    () => ({
      buttonList: [
        ["undo", "redo"],
        ["formatBlock"],
        ["font", "fontSize"],
        ["bold", "italic", "underline", "strike"],
        ["fontColor", "hiliteColor"],
        ["align", "list", "lineHeight"],
        ["link", "table"],
        ["fullScreen", "showBlocks"],
        ["removeFormat"],
      ],
      removeFormatTags: [
        "b",
        "strong",
        "span",
        "font",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
      ],
      removeFormatAttributes: ["style", "class"],
      templates: [
        {
          name: "50% Text + 50% Image",
          html: `
          <div class="row-50-50">
            <div class="col text">
              <p>✍ Write your text here...</p>
            </div>
            <div class="col image">
              <img src="" alt="image" />
            </div>
          </div>
        `,
        },
      ],
    }),
    []
  );

  console.log("this is page name", pageName);

  const openConfirm = (callback) => {
    setOnConfirm(() => callback);
    setConfirmOpen(true);
  };

  const handleConfirmYes = () => {
    if (onConfirm) onConfirm();
    setConfirmOpen(false);
  };

  const handleConfirmNo = () => {
    setConfirmOpen(false);
    setOnConfirm(null);
  };

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={<GridExpandMoreIcon />}
        aria-controls="panel-content"
        id="panel-header"
      >
        <Typography fontWeight="600">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {/* Image Upload */}

        <div className="form_comp">
          <label>Page Name</label>
          <input
            type="text"
            value={data.pageName || ""}
            onChange={(e) =>
              setData((prev) => ({
                ...prev,
                pageName: e.target.value.toLowerCase(),
              }))
            }
            placeholder="Enter page name"
            style={{
              width: "100%",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              marginTop: "4px",
            }}
          />
        </div>

        <div className="form_comp">
          {/* <label>Upload Banner Image</label> */}
          {/* <input type="file" accept="image/*" onChange={handleImageUpload} /> */}
          <div className="form_comp">
            <label>Upload Web Images</label>

            <Button
              variant="outlined"
              size="small"
              onClick={() => webImageRef.current.click()}
            >
              ADD
            </Button>

            <input
              ref={webImageRef}
              type="file"
              accept="image/*"
              hidden
              multiple
              onChange={handleImageUpload("webImages")}
            />
            {errors.webImages && (
              <p style={{ color: "red" }}>{errors.webImages}</p>
            )}
            {/* Preview */}
            <div style={{ marginTop: 10 }}>
              {data.webImages?.map((img, i) => (
                <div
                  key={i}
                  style={{ display: "inline-block", marginRight: 10 }}
                >
                  <img src={img} width={120} style={{ borderRadius: 6 }} />

                  <Button
                    size="small"
                    color="error"
                    onClick={() =>
                      openConfirm(() =>
                        setData((prev) => ({
                          ...prev,
                          webImages: prev.webImages.filter(
                            (_, idx) => idx !== i
                          ),
                        }))
                      )
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="form_comp">
          <label>Upload Mobile Images</label>

          <Button
            variant="outlined"
            size="small"
            onClick={() => mobileImageRef.current.click()}
          >
            ADD
          </Button>

          <input
            ref={mobileImageRef}
            type="file"
            accept="image/*"
            hidden
            multiple
            onChange={handleImageUpload("mobileImages")}
          />
          {errors.mobileImages && (
            <p style={{ color: "red" }}>{errors.mobileImages}</p>
          )}
          <div style={{ marginTop: 10 }}>
            {data.mobileImages?.map((img, i) => (
              <div key={i} style={{ display: "inline-block", marginRight: 10 }}>
                <img src={img} width={120} style={{ borderRadius: 6 }} />

                <Button
                  size="small"
                  color="error"
                  onClick={() =>
                    openConfirm(() =>
                      setData((prev) => ({
                        ...prev,
                        mobileImages: prev.mobileImages.filter(
                          (_, idx) => idx !== i
                        ),
                      }))
                    )
                  }
                >
                  <DeleteIcon fontSize="small" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Video Upload */}
        <div className="form_comp">
          <label>Upload Web Videos</label>

          <Button
            variant="outlined"
            size="small"
            disabled={uploadingVideo.webVideos}
            onClick={() => webVideoRef.current.click()}
            startIcon={
              uploadingVideo.webVideos ? <CircularProgress size={14} /> : null
            }
          >
            {uploadingVideo.webVideos ? "Uploading..." : "ADD"}
          </Button>

          <input
            ref={webVideoRef}
            type="file"
            accept="video/*"
            hidden
            multiple
            onChange={handleVideoUpload("webVideos")}
          />
          {errors.webVideos && (
            <p style={{ color: "red" }}>{errors.webVideos}</p>
          )}

          {/* Preview */}
          <div style={{ marginTop: 10 }}>
            {data.webVideos?.map((video, i) => (
              <div key={i} style={{ display: "inline-block", marginRight: 10 }}>
                <video
                  src={video}
                  width={160}
                  controls
                  style={{ borderRadius: 6 }}
                />

                <Button
                  size="small"
                  color="error"
                  onClick={() =>
                    openConfirm(() =>
                      setData((prev) => ({
                        ...prev,
                        webVideos: prev.webVideos.filter((_, idx) => idx !== i),
                      }))
                    )
                  }
                >
                  <DeleteIcon fontSize="small" />
                </Button>
              </div>
            ))}
          </div>
        </div>
        {/* upload Mobile videos*/}

        <div className="form_comp">
          <label>Upload Mobile Videos</label>

          <Button
            variant="outlined"
            size="small"
            disabled={uploadingVideo.mobileVideos}
            onClick={() => mobileVideoRef.current.click()}
            startIcon={
              uploadingVideo.mobileVideos ? (
                <CircularProgress size={14} />
              ) : null
            }
          >
            {uploadingVideo.mobileVideos ? "Uploading..." : "ADD"}
          </Button>

          <input
            ref={mobileVideoRef}
            type="file"
            accept="video/*"
            hidden
            multiple
            onChange={handleVideoUpload("mobileVideos")}
          />
          {errors.mobileVideos && (
            <p style={{ color: "red" }}>{errors.mobileVideos}</p>
          )}

          {/* Preview */}
          <div style={{ marginTop: 10 }}>
            {data.mobileVideos?.map((video, i) => (
              <div key={i} style={{ display: "inline-block", marginRight: 10 }}>
                <video
                  src={video}
                  width={160}
                  controls
                  style={{ borderRadius: 6 }}
                />

                <Button
                  size="small"
                  color="error"
                  onClick={() =>
                    openConfirm(() =>
                      setData((prev) => ({
                        ...prev,
                        mobileVideos: prev.mobileVideos.filter(
                          (_, idx) => idx !== i
                        ),
                      }))
                    )
                  }
                >
                  <DeleteIcon fontSize="small" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Banner Title */}
        <div className="form_comp">
          <label>
            Banner Title<span style={{ color: "red" }}>*</span>
          </label>

          <SunEditor
            getSunEditorInstance={(editor) => {
              editorRef.current = editor;
            }}
            onChange={(content) => {
              setData((prev) => ({
                ...prev,
                seoContent: content, // typing updates state ONLY
              }));
            }}
            height="300px"
            setOptions={editorOptions}
          />
        </div>

        {/* Error */}
      </AccordionDetails>
      <Dialog open={confirmOpen} onClose={handleConfirmNo}>
        <Typography sx={{ p: 3, fontWeight: 600 }}>
          Are you sure you want to delete this item?
        </Typography>

        <div
          style={{ display: "flex", justifyContent: "flex-end", padding: 16 }}
        >
          <Button onClick={handleConfirmNo}>No</Button>
          <Button color="error" variant="contained" onClick={handleConfirmYes}>
            Yes, Delete
          </Button>
        </div>
      </Dialog>
    </Accordion>
  );
};

export default BannerAccordion;
